import type { ReactNode } from 'react'
import { LoadingSkeleton, EmptyState, ErrorState } from './States'
import { Pagination } from './Pagination'

export interface Column<T> {
  key: string
  header: string
  render: (row: T) => ReactNode
  sortable?: boolean
  className?: string
}

interface DataTableProps<T> {
  columns: Column<T>[]
  rows: T[]
  getRowId: (row: T) => string
  isLoading: boolean
  isError: boolean
  errorMessage?: string
  onRetry?: () => void
  emptyTitle: string
  emptyDescription?: string
  onRowClick?: (row: T) => void
  page?: number
  totalPages?: number
  totalItems?: number
  onPageChange?: (page: number) => void
  sortField?: string
  sortDir?: 'asc' | 'desc'
  onSortChange?: (field: string, dir: 'asc' | 'desc') => void
}

export function DataTable<T>({
  columns,
  rows = [],
  getRowId,
  isLoading,
  isError,
  errorMessage,
  onRetry,
  emptyTitle,
  emptyDescription,
  onRowClick,
  page,
  totalPages,
  totalItems,
  onPageChange,
  sortField,
  sortDir,
  onSortChange,
}: DataTableProps<T>) {
  const safeRows = Array.isArray(rows) ? rows : []

  if (isError) {
    return <ErrorState message={errorMessage ?? 'Could not load this data. Try again.'} onRetry={onRetry} />
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-xs overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[720px] border-collapse text-xs">
          <thead>
            <tr className="bg-slate-50/90 border-b border-slate-200">
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={`px-4 py-3.5 text-left text-[10.5px] font-bold uppercase tracking-wider text-slate-500 ${col.className ?? ''} ${
                    col.sortable ? 'cursor-pointer select-none hover:text-slate-900' : ''
                  }`}
                  onClick={() => {
                    if (!col.sortable || !onSortChange) return
                    const nextDir = sortField === col.key && sortDir === 'asc' ? 'desc' : 'asc'
                    onSortChange(col.key, nextDir)
                  }}
                >
                  <div className="flex items-center gap-1">
                    <span>{col.header}</span>
                    {col.sortable && sortField === col.key && (
                      <span className="text-sky-600 font-bold">{sortDir === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          {!isLoading && safeRows.length > 0 && (
            <tbody className="divide-y divide-slate-100 text-slate-800">
              {safeRows.map((row, index) => (
                <tr
                  key={getRowId(row)}
                  onClick={() => onRowClick?.(row)}
                  className={`transition-all duration-100 ${
                    onRowClick ? 'cursor-pointer hover:bg-sky-50/30' : ''
                  } ${index % 2 === 1 ? 'bg-slate-50/20' : 'bg-white'}`}
                >
                  {columns.map((col) => (
                    <td key={col.key} className={`px-4 py-3.5 align-middle font-medium ${col.className ?? ''}`}>
                      {col.render(row)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          )}
        </table>
        {isLoading && <LoadingSkeleton rows={8} />}
      </div>

      {!isLoading && safeRows.length === 0 && (
        <EmptyState title={emptyTitle} description={emptyDescription} />
      )}

      {!isLoading && safeRows.length > 0 && page !== undefined && totalPages !== undefined && onPageChange && (
        <div className="border-t border-slate-200 bg-slate-50/70 px-4 py-3">
          <Pagination page={page} totalPages={totalPages} totalItems={totalItems ?? safeRows.length} onPageChange={onPageChange} />
        </div>
      )}
    </div>
  )
}
